import React from 'react';
import {
  Document as PDFDocument,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
  Svg,
  Path,
  Circle,
  Polygon,
} from '@react-pdf/renderer';
import {
  Document,
  Block,
  DocumentHeaderFields,
  HeadingBlockData,
  ParagraphBlockData,
  ScopeListBlockData,
  BulletListBlockData,
  TableBlockData,
  SpacerBlockData,
  ImageBlockData,
  ImageAppendixBlockData,
  SignatureBlockData,
  InstructionListBlockData,
  DrawingListBlockData,
  QaListBlockData,
  designCriteriaSentence,
  DRAWINGS_INTRO,
  RowFragment,
  instructionRowBlocks,
  DocumentChromeVariant,
  signatureSpaceLines,
  formatDateLongParts,
  getAppendixStartNumber,
  getBlockSpacing,
  ParagraphFragment,
  SignatureStamp,
  TableAppendixBlockData,
  PlanAppendixBlockData,
  itemNumbers,
  photoNumbers,
  planPages,
  tableImageCell,
  visibleTableColumns,
  listMarker,
  listStyleOf,
  CERTIFICATE_FOOTER_SRC,
  displayJobNumberOf,
  displayProjectDescriptionOf,
  DocumentContact,
  effectiveContact,
} from '../../config';
import type { ParagraphLayout, LayoutRun } from '../../utils/measureParagraphLines';
import { sliceHtml } from '../../utils/htmlSlice';
import { PDF_DOCUMENT_FONT, PDF_ASSET_BASE } from '../../../../utils/pdfFonts';
import { buildClientAddressLines, resolveCareOf } from '../../../InvoiceRegister/config';
import { SI_TOKENS, distributionTickInnerPt } from '../../siChromeTokens';
import { APPENDIX_TOKENS } from '../../appendixTokens';
import { photoAppendixPages, photoFrame, photoGrid, photoLabelWidth, PhotoFragment } from '../../photoAppendixLayout';
import { fitPlanBox, markerGeometry, markerLabel } from '../../utils/planMarkers';
import { tableCellLayoutKey } from '../AppendixPages/TableAppendixPage';
import { PLAN_BOX_HEIGHT_MM, PLAN_BOX_WIDTH_MM } from '../AppendixPages/PlanAppendixPage';
import { ORDINAL_SIZE_RATIO, type LongDateParts } from '../../dateOrdinal';
import { SUBSCRIPT_SIZE_RATIO, hasSubscripts, parseSubscripts } from '../../subscript';

/** One entry of this file's stylesheet, for helpers that take a style. */
type PdfStyle = (typeof styles)[keyof typeof styles];

// =======================================
// FONTS
// =======================================
//
// Registered once in utils/pdfFonts for every PDF in the app: Tinos, the same
// four TTF files the builder loads through public/fonts/tinos.css, so react-pdf
// measures text exactly as the browser does. That import also switches off
// react-pdf's hyphenation.
const DOCUMENT_FONT = PDF_DOCUMENT_FONT;
const ASSET_BASE = PDF_ASSET_BASE;

// =======================================
// UNITS & PAGE GEOMETRY
// =======================================
//
// Everything mirrors document-editor.scss, which in turn mirrors the firm's
// Word letter template: Times 11pt, single spacing (1.15 x size for Times
// metrics), margins 32.5 / 25.4 / 22 / 30 mm, and blank lines as the only
// vertical spacing. The builder's px values convert at 0.75 px -> pt.
const px = (value: number) => value * 0.75;
const mmToPt = (mm: number) => mm * 2.83465;

const FONT_SIZE_PT = 11;
const LINE_HEIGHT = 1.15;
const LINE_PT = FONT_SIZE_PT * LINE_HEIGHT; // one blank line

// Pair a font size with its leading. react-pdf resolves a unitless lineHeight
// against the fontSize in the SAME style object and then inherits the resulting
// ABSOLUTE value, so a style that sets only fontSize silently keeps the page's
// leading -- text drifts further down the more lines precede it, and only the
// PDF is wrong (browsers re-resolve per element). Declaring the pair together is
// the fix (DEVELOPMENT.md parity rule 5).
const type = (sizePt: number) => ({ fontSize: sizePt, lineHeight: LINE_HEIGHT });

// Sizes a subscript inside these blocks is scaled from (withSubscripts).
const HEADING_SIZE_PT: Record<number, number> = { 1: FONT_SIZE_PT * 1.3, 2: FONT_SIZE_PT * 1.15, 3: FONT_SIZE_PT };
const IMAGE_CAPTION_SIZE_PT = FONT_SIZE_PT * 0.9;

const PAGE_MARGIN_TOP_MM = 32.5;
const PAGE_MARGIN_RIGHT_MM = 25.4;
// Content stops here on every page. The builder paginates against the same
// limit (FOOTER_CLEARANCE_MM in DocumentEditor) so nothing runs past this box.
const PAGE_MARGIN_BOTTOM_MM = 22;
const PAGE_MARGIN_LEFT_MM = 30;
const LETTERHEAD_GAP_MM = 3.6; // letterhead bottom to date line (date at 37mm)
// Where both footers sit: their bottom edge, measured up from the paper edge,
// inside the 22mm bottom margin. The page numbers and a certificate's trustee
// line share it, so the two sit on the same line. $page-footer-bottom in
// document-editor.scss -- change both together.
const PAGE_FOOTER_BOTTOM_MM = 10;
// A certificate's trustee line: right-aligned to the text column, ~6mm clear
// of the content box. 110mm wide puts its text at about 8pt.
// $certificate-footer-width in document-editor.scss -- change both together.
const CERTIFICATE_FOOTER_WIDTH_MM = 110;
const CONTENT_WIDTH_PT = mmToPt(210 - PAGE_MARGIN_LEFT_MM - PAGE_MARGIN_RIGHT_MM); // 154.6mm

// react-pdf sizes a `wrap={false}` page to its content (shorter or taller than
// A4) and ignores a height on the page style itself. Each page therefore holds
// one fixed-height box (A4 minus the vertical paddings) so the page is always
// exactly A4; content that would overflow that box draws past it instead of
// stretching the page.
const A4_HEIGHT_PT = mmToPt(297);
const BODY_BOX_HEIGHT_PT = A4_HEIGHT_PT - mmToPt(PAGE_MARGIN_TOP_MM) - mmToPt(PAGE_MARGIN_BOTTOM_MM);
const APPENDIX_BOX_HEIGHT_PT = A4_HEIGHT_PT - mmToPt(15) - mmToPt(30);

// Colours used by the builder stylesheet (sass/_variables.scss)
const COLOR_TEXT_MUTED = '#757575';
const COLOR_TEXT_SECONDARY = '#6b7280';
const COLOR_BORDER = '#ddd';
const COLOR_PRIMARY = '#007bff';
const COLOR_GRAY_50 = '#f9fafb';

// =======================================
// STYLES - Matching editor layout
// =======================================

const styles = StyleSheet.create({
  page: {
    paddingTop: mmToPt(PAGE_MARGIN_TOP_MM),
    paddingBottom: mmToPt(PAGE_MARGIN_BOTTOM_MM),
    paddingLeft: mmToPt(PAGE_MARGIN_LEFT_MM),
    paddingRight: mmToPt(PAGE_MARGIN_RIGHT_MM),
    fontSize: FONT_SIZE_PT,
    fontFamily: DOCUMENT_FONT,
    lineHeight: LINE_HEIGHT,
    flexDirection: 'column',
  },
  // Letterhead image (first page only) - bleeds to the page edges
  letterhead: {
    marginLeft: mmToPt(-PAGE_MARGIN_LEFT_MM),
    marginRight: mmToPt(-PAGE_MARGIN_RIGHT_MM),
    marginTop: mmToPt(-PAGE_MARGIN_TOP_MM),
    marginBottom: mmToPt(LETTERHEAD_GAP_MM),
    width: mmToPt(210),
  },
  // Fixed-height box that fills the page between the paddings. The letterhead
  // (negative top margin) and all content live inside it, so page 1 is A4 too.
  pageBox: {
    height: BODY_BOX_HEIGHT_PT,
    flexDirection: 'column',
  },
  pageContent: {
    flexDirection: 'column',
    flexShrink: 0,
  },
  // Header section - blank-line rhythm from the letter template:
  //   date 2 / client 1 / ATTENTION 2 / Dear 1 / Re: 1, then the body
  headerSection: {
    marginBottom: LINE_PT,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: LINE_PT * 2,
  },
  clientSection: {
    marginBottom: LINE_PT,
  },
  clientAddress: {
    textTransform: 'uppercase',
  },
  attentionRow: {
    marginBottom: LINE_PT * 2,
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  dearRow: {
    marginBottom: LINE_PT,
  },
  referenceRow: {
    flexDirection: 'row',
  },
  referenceLabel: {
    width: mmToPt(12.7), // hanging indent of 720 twips in the letter
    fontWeight: 'bold',
  },
  referenceContent: {
    flex: 1,
    textTransform: 'uppercase',
  },
  // Each Re: line keeps its height when the value behind it is blank.
  //
  // DocumentChrome always draws all three -- as "[REPORT TITLE]" or
  // "[Project Address]" when there is nothing to show -- so without this an
  // empty one collapses here and every block in the document sits 12.65pt too
  // high in the export. The placeholder itself is deliberately NOT printed: it
  // is an editing hint, and a signed PDF must not carry it.
  referenceLine: {
    fontWeight: 'bold',
    minHeight: LINE_PT,
  },
  bold: {
    fontWeight: 'bold',
  },

  // A long date, laid out as a row so the ordinal suffix can carry its own
  // size. Deliberately NOT a nested <Text>: react-pdf gives every run in a
  // Text the same baseline, so the suffix could only be made smaller, not
  // raised.
  longDate: {
    flexDirection: 'row',
  },
  // The suffix sets a size and NOTHING else -- no lineHeight, no margin, no
  // verticalAlign. This is the one place parity rule 5 is inverted on purpose:
  // inheriting the surrounding absolute leading is exactly what is wanted,
  // because it keeps the row the same height as the plain line it replaced,
  // and react-pdf then raises the smaller run on its own (see dateOrdinal.ts).
  // Adding type() here would not move the suffix, but adding a margin would.
  dateOrdinal: {
    fontSize: FONT_SIZE_PT * ORDINAL_SIZE_RATIO,
  },
  siDateOrdinal: {
    fontSize: SI_TOKENS.fieldValueSizePt * ORDINAL_SIZE_RATIO,
  },

  // -----------------------------------------------------------------
  // SITE INSTRUCTION CHROME
  // -----------------------------------------------------------------
  // Every number and colour comes from siChromeTokens, which document-chrome.scss
  // consumes as CSS custom properties. Do not write a literal here.
  //
  // Every style that sets a fontSize spreads type() with it: react-pdf resolves
  // a unitless lineHeight against the fontSize in the SAME style object and then
  // inherits the ABSOLUTE result, so a size without its leading silently keeps
  // the page's 11pt leading (parity rule 5).
  siHeader: {
    marginBottom: 0,
  },
  siTitle: {
    ...type(SI_TOKENS.titleSizePt),
    lineHeight: 1.05,
    fontWeight: 'bold',
    letterSpacing: SI_TOKENS.titleLetterSpacingPt,
    color: SI_TOKENS.ink,
    marginBottom: SI_TOKENS.titleGapPt,
  },
  siFields: {
    marginBottom: SI_TOKENS.fieldsGapPt,
  },
  siFieldRow: {
    flexDirection: 'row',
    marginBottom: SI_TOKENS.fieldRowGapPt,
  },
  siFieldRowLast: {
    flexDirection: 'row',
  },
  siFieldCellWide: {
    flex: 1,
    marginRight: SI_TOKENS.fieldColumnGapPt,
  },
  siFieldCellNarrow: {
    width: SI_TOKENS.rightColumnWidthPt,
    flexGrow: 0,
    flexShrink: 0,
  },
  siFieldCellFill: {
    flex: 1,
  },
  siFieldPair: {
    flexDirection: 'row',
  },
  siFieldPairGap: {
    width: SI_TOKENS.fieldPairGapPt,
    flexGrow: 0,
    flexShrink: 0,
  },
  siFieldLabel: {
    ...type(SI_TOKENS.fieldLabelSizePt),
    lineHeight: 1.2,
    fontWeight: 'bold',
    letterSpacing: SI_TOKENS.fieldLabelLetterSpacingPt,
    color: SI_TOKENS.labelInk,
    marginBottom: SI_TOKENS.fieldLabelGapPt,
  },
  siFieldRule: {
    borderBottomWidth: 0.75,
    borderBottomColor: SI_TOKENS.rule,
    borderBottomStyle: 'solid',
    paddingBottom: SI_TOKENS.fieldValuePadPt,
  },
  siFieldValue: {
    ...type(SI_TOKENS.fieldValueSizePt),
    lineHeight: 1.2,
    color: SI_TOKENS.ink,
  },
  siNotice: {
    backgroundColor: SI_TOKENS.noticeBg,
    padding: SI_TOKENS.noticePadPt,
    marginBottom: SI_TOKENS.noticeGapPt,
  },
  siNoticeText: {
    ...type(SI_TOKENS.noticeSizePt),
    lineHeight: 1.35,
    color: SI_TOKENS.ink,
  },
  siNoticeTextSecond: {
    marginTop: SI_TOKENS.noticeLineGapPt,
  },
  siTableHead: {
    flexDirection: 'row',
    backgroundColor: SI_TOKENS.tableHeadBg,
    padding: SI_TOKENS.tableHeadPadPt,
  },
  siTableHeadCell: {
    ...type(SI_TOKENS.tableHeadSizePt),
    lineHeight: 1.2,
    fontWeight: 'bold',
    letterSpacing: SI_TOKENS.tableHeadLetterSpacingPt,
    // react-pdf does not inherit contrast from the background; this must be
    // stated, and document-chrome.scss states the same colour.
    color: SI_TOKENS.tableHeadInk,
  },
  siTableHeadRef: {
    width: SI_TOKENS.refColumnWidthPt,
    flexGrow: 0,
    flexShrink: 0,
  },
  // Instruction rows -- the REF column matches siTableHeadRef and the editor's
  // .instruction-row__ref so all three line up.
  siRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingTop: SI_TOKENS.tableHeadPadPt,
    paddingBottom: SI_TOKENS.tableHeadPadPt,
    paddingLeft: SI_TOKENS.tableHeadPadPt,
    paddingRight: SI_TOKENS.tableHeadPadPt,
    borderBottomWidth: SI_TOKENS.rowRuleWidthPt,
    borderBottomColor: SI_TOKENS.rowRule,
    borderBottomStyle: 'solid',
  },
  siRowRef: {
    width: SI_TOKENS.refColumnWidthPt,
    flexGrow: 0,
    flexShrink: 0,
  },
  siRowContent: {
    flex: 1,
    minHeight: LINE_PT,
  },
  // Site instruction footer: signature left, distribution right, one line.
  // Mirrors .signature-block--footer in signature-block.scss.
  signatureFooterRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  signatureFooterColumn: {
    width: SI_TOKENS.signatureWidthPt,
    flexGrow: 0,
    flexShrink: 0,
  },
  siDistributionLabel: {
    ...type(SI_TOKENS.fieldLabelSizePt),
    lineHeight: 1.2,
    fontWeight: 'bold',
    letterSpacing: SI_TOKENS.fieldLabelLetterSpacingPt,
    color: SI_TOKENS.labelInk,
    marginBottom: SI_TOKENS.distributionHeadingGapPt,
  },
  siDistributionItems: {
    // Shrink-to-fit, like the editor's inline-flex column, so the boxes sit
    // just after the longest label instead of at the page edge.
    alignSelf: 'flex-start',
  },
  siDistributionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: SI_TOKENS.distributionRowGapPt,
  },
  siDistributionBox: {
    width: SI_TOKENS.distributionBoxPt,
    height: SI_TOKENS.distributionBoxPt,
    marginLeft: SI_TOKENS.distributionLabelGapPt,
    borderWidth: SI_TOKENS.distributionBoxBorderPt,
    borderColor: SI_TOKENS.ink,
    borderStyle: 'solid',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Block spacing (block.spacing): one blank line above/below, on by default.
  // Margins are applied in renderBlocks, which collapses neighbours' margins
  // like the browser does.
  blockWrapper: {
    marginTop: 0,
    marginBottom: 0,
  },
  // Headings (heading-block.scss) - sized relative to the body, no margins
  heading1: {
    ...type(HEADING_SIZE_PT[1]),
    fontWeight: 'bold',
  },
  heading2: {
    ...type(HEADING_SIZE_PT[2]),
    fontWeight: 'bold',
  },
  heading3: {
    ...type(HEADING_SIZE_PT[3]),
    fontWeight: 'bold',
  },
  // Paragraph (.paragraph-content: padding 0 2px, min-height one line, justified)
  // Certified drawings on an RWD Design Certificate. The bullet column is
  // listBullet's, so a drawing row's marker lines up with the standards list
  // above it; the issue and title columns are the Word original's tab stops,
  // 45mm and 65mm from the left margin. The same millimetre values live in
  // drawing-list-block.scss, which cannot import this module -- change both
  // together or the measured row stops matching the drawn one. Everything else
  // inherits the page's body type, because the editor measured these rows at
  // the body size.
  drawingCriteria: {
    marginBottom: LINE_PT,
  },
  drawingIntro: {
    marginBottom: LINE_PT,
  },
  drawingRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  drawingBullet: {
    width: mmToPt(12.7),
    textAlign: 'right',
    paddingRight: px(7), // identical to listBullet
  },
  drawingNumber: {
    width: mmToPt(32.3),
  },
  drawingIssue: {
    width: mmToPt(20),
  },
  drawingTitle: {
    flex: 1,
  },
  drawingEmpty: {
    fontStyle: 'italic',
  },
  // The QA results an As Constructed Certificate relies on. The bullet column
  // is listBullet's, so a QA bullet lines up with any bullet list in the document;
  // the dash sits one level in again, in a column of the same width, which is
  // the second-level hanging indent the Word originals set. The same
  // millimetre value lives in qa-list-block.scss, which cannot import this
  // module -- change both together.
  qaLine: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  qaBullet: {
    width: mmToPt(12.7),
    textAlign: 'right',
    paddingRight: px(7), // identical to listBullet
  },
  qaIndent: {
    width: mmToPt(12.7),
  },
  qaText: {
    flex: 1,
    // The editor always draws this line, even when the author has emptied it.
    // Without the reserved line an empty one collapses here and everything
    // below moves up -- the signatoryName bug, prevented rather than found.
    minHeight: LINE_PT,
  },
  qaEmpty: {
    fontStyle: 'italic',
  },
  paragraph: {
    paddingLeft: px(2),
    paddingRight: px(2),
    minHeight: LINE_PT,
  },
  paragraphText: {
    textAlign: 'justify',
  },
  // Paragraph rendered from browser-measured lines: each line is its own Text
  // with the exact word spacing Chrome used. The box is allowed to extend a
  // little past the right edge so a sub-point measuring difference between
  // Chrome and fontkit can never push the last word onto a new line.
  paragraphMeasured: {
    paddingLeft: px(2),
    marginRight: -mmToPt(3),
    minHeight: LINE_PT,
  },
  // Bullet list (bullet-list-block.scss: ul padding-left 12.7mm, li padding 0)
  listItem: {
    flexDirection: 'row',
  },
  listBullet: {
    width: mmToPt(12.7),
    textAlign: 'right',
    paddingRight: px(7), // marker sits just left of the content edge
  },
  listContent: {
    flex: 1,
    paddingRight: px(8), // .item-text width: calc(100% - 8px)
  },
  // Scope list (scope-list-block.scss)
  scopeItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  scopeBullet: {
    width: px(12 + 8), // min-width 12px + gap 8px
    color: COLOR_TEXT_SECONDARY,
    fontWeight: 'bold',
  },
  scopeText: {
    flex: 1,
    paddingLeft: px(2),
    paddingRight: px(2),
  },
  scopeIndent1: { paddingLeft: px(24) },
  scopeIndent2: { paddingLeft: px(48) },
  scopeIndent3: { paddingLeft: px(72) },
  checkbox: {
    width: px(14),
    height: px(14),
    marginRight: px(8), // gap
    marginTop: px(1),
    borderWidth: px(2),
    borderColor: '#b5b4b2', // rgba(55, 53, 47, 0.3) on white
    borderRadius: px(3),
  },
  checkboxChecked: {
    backgroundColor: COLOR_PRIMARY,
    borderColor: COLOR_PRIMARY,
  },
  completedText: {
    textDecoration: 'line-through',
    color: COLOR_TEXT_MUTED,
  },
  // Table (table-block.scss: 1px #ddd borders, 8px cell padding)
  table: {
    borderWidth: px(1),
    borderColor: COLOR_BORDER,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCell: {
    padding: px(8),
    borderWidth: px(1),
    borderColor: COLOR_BORDER,
    minHeight: px(8 * 2) + FONT_SIZE_PT * 1.4, // padding + .cell-content min-height 1.4em
  },
  tableHeaderCell: {
    backgroundColor: COLOR_GRAY_50,
    fontWeight: 'bold',
  },
  // Divider (divider-block.scss: hr margin 0.575em 0 = one line in total)
  divider: {
    borderBottomWidth: px(1),
    borderBottomColor: COLOR_BORDER,
    marginTop: LINE_PT / 2,
    marginBottom: LINE_PT / 2,
  },
  // Spacer (spacer-block.scss) - whole blank lines
  spacerSmall: { height: LINE_PT },
  spacerMedium: { height: LINE_PT * 2 },
  spacerLarge: { height: LINE_PT * 4 },
  // Image block (image-block.scss)
  imageContainer: {
    alignItems: 'center',
  },
  imageContainerLeft: {
    alignItems: 'flex-start',
  },
  imageContainerRight: {
    alignItems: 'flex-end',
  },
  imageWrapper: {
    borderRadius: px(4),
    overflow: 'hidden',
  },
  imageCaption: {
    ...type(IMAGE_CAPTION_SIZE_PT),
    color: COLOR_TEXT_SECONDARY,
    marginTop: 0,
    textAlign: 'center',
  },
  // Photo label line under appendix images
  imageLabelRow: {
    marginTop: APPENDIX_TOKENS.photoLabelGapPt,
  },
  imageLabelLine: {
    fontSize: APPENDIX_TOKENS.photoLabelSizePt,
    color: '#666',
    lineHeight: APPENDIX_TOKENS.photoLabelLineHeight,
  },
  photoLabel: {
    fontSize: APPENDIX_TOKENS.photoLabelSizePt,
    fontWeight: 'bold',
    color: '#000',
  },
  // Appendix page - different padding (matches .appendix-content in the builder)
  appendixPage: {
    paddingTop: mmToPt(15),
    paddingBottom: mmToPt(30),
    paddingLeft: mmToPt(20),
    paddingRight: mmToPt(20),
    fontSize: FONT_SIZE_PT,
    fontFamily: DOCUMENT_FONT,
    lineHeight: LINE_HEIGHT,
    flexDirection: 'column',
  },
  appendixContent: {
    flexDirection: 'column',
    height: APPENDIX_BOX_HEIGHT_PT, // 297mm - 15mm top - 30mm bottom padding = 252mm
  },
  appendixGrid: {
    flexDirection: 'column',
  },
  // $border-radius (4px) in the editor.
  appendixImageWrapper: {
    position: 'absolute',
    borderRadius: 3,
    overflow: 'hidden',
  },
  // Table and plan appendices (appendix-pages.scss / table-appendix-page.scss).
  // Every number here is an APPENDIX_TOKENS value, the same one the editor's
  // stylesheet consumes as a custom property -- these pages are paginated on
  // heights the editor measured, so the two must agree to the point.
  apxTitle: {
    fontSize: APPENDIX_TOKENS.titleSizePt,
    lineHeight: APPENDIX_TOKENS.titleLineHeight,
    fontWeight: 'bold',
    paddingTop: APPENDIX_TOKENS.titlePadPt,
    paddingBottom: APPENDIX_TOKENS.titlePadPt,
    paddingLeft: APPENDIX_TOKENS.titlePadXPt,
    paddingRight: APPENDIX_TOKENS.titlePadXPt,
    marginBottom: APPENDIX_TOKENS.titleGapPt,
    color: '#111827',
  },
  apxTable: {
    width: '100%',
    flexDirection: 'column',
    color: APPENDIX_TOKENS.cellInk,
  },
  apxHead: {
    flexDirection: 'row',
    borderBottomWidth: APPENDIX_TOKENS.ruleWidthPt,
    borderBottomColor: APPENDIX_TOKENS.headRule,
  },
  apxRow: {
    flexDirection: 'row',
    borderBottomWidth: APPENDIX_TOKENS.ruleWidthPt,
    borderBottomColor: APPENDIX_TOKENS.rowRule,
  },
  apxRowZebra: {
    backgroundColor: APPENDIX_TOKENS.zebra,
  },
  apxCell: {
    paddingTop: APPENDIX_TOKENS.cellPadPt,
    paddingBottom: APPENDIX_TOKENS.cellPadPt,
    paddingLeft: APPENDIX_TOKENS.cellPadXPt,
    paddingRight: APPENDIX_TOKENS.cellPadXPt,
  },
  apxCellText: {
    fontSize: APPENDIX_TOKENS.cellSizePt,
    lineHeight: APPENDIX_TOKENS.lineHeight,
    minHeight: APPENDIX_TOKENS.cellSizePt * APPENDIX_TOKENS.lineHeight,
  },
  apxHeadText: {
    fontSize: APPENDIX_TOKENS.headSizePt,
    lineHeight: APPENDIX_TOKENS.lineHeight,
    minHeight: APPENDIX_TOKENS.headSizePt * APPENDIX_TOKENS.lineHeight,
    fontWeight: 'bold',
    color: APPENDIX_TOKENS.headInk,
  },
  apxBold: {
    fontWeight: 'bold',
  },
  // Signature (signature-block.scss) - laid out like the letter template.
  // position: relative anchors the absolutely positioned signature image: it
  // takes no space in flow (and may overhang the space, over the rule and the
  // name, like a wet signature), so a signed block is exactly as tall as an
  // unsigned one regardless of the signer's placement. The image is the
  // section's LAST child because react-pdf paints in tree order -- rendered
  // earlier, the rule and name would print over the ink, while the browser
  // paints positioned elements on top of them.
  signatureSection: {
    marginTop: 0,
    position: 'relative',
  },
  signatureSpace: {
    height: LINE_PT * 2, // two blank lines for the handwritten signature
  },
  signatureLine: {
    width: mmToPt(62),
    height: LINE_PT,
    borderBottomWidth: px(1),
    borderBottomColor: '#111827',
  },
  signatureImageFallback: {
    // Used only when the image's aspect ratio could not be measured (needed to
    // size an absolutely positioned react-pdf Image): the pre-placement layout,
    // contained inside the space.
    height: LINE_PT * 2,
    maxWidth: mmToPt(62),
    objectFit: 'contain',
  },
  signatoryName: {
    ...type(FONT_SIZE_PT),
    fontWeight: 'bold',
    // The name's line is reserved even before a signatory is chosen. The editor
    // always draws it -- as the "Select signatory..." placeholder -- so without
    // this an unsigned document measures one line taller than it exports, and
    // anything below the signature block sits 12.6pt too low in the PDF. Never
    // visible while the signature was the last block on the page; the design
    // certificate, which had "Encl." after it, was the first to show it.
    minHeight: LINE_PT,
  },
  signatoryMeta: {
    ...type(FONT_SIZE_PT),
  },
  // Page footer (.page-footer in document-editor.scss). Sits inside the bottom
  // margin, PAGE_FOOTER_BOTTOM_MM off the paper edge -- the same height the
  // invoice's footer sits at (invoiceLayout.PAGE_FOOTER_BOTTOM_MM). No rule
  // above it, matching the editor.
  pageFooter: {
    position: 'absolute',
    bottom: mmToPt(PAGE_FOOTER_BOTTOM_MM),
    left: mmToPt(PAGE_MARGIN_LEFT_MM),
    right: mmToPt(PAGE_MARGIN_RIGHT_MM),
    fontSize: px(12),
    lineHeight: LINE_HEIGHT,
    color: COLOR_TEXT_MUTED,
    textAlign: 'center',
  },
  // Width only: react-pdf takes the height from the image's own proportions,
  // as the editor's <img> does.
  certificateFooter: {
    position: 'absolute',
    bottom: mmToPt(PAGE_FOOTER_BOTTOM_MM),
    right: mmToPt(PAGE_MARGIN_RIGHT_MM),
    width: mmToPt(CERTIFICATE_FOOTER_WIDTH_MM),
  },
});

// =======================================
// INTERFACES
// =======================================

// One entry on a page: a whole block, one piece of a paragraph the builder cut
// between lines, or one slice of an instruction list it cut between items.
interface PageItem {
  blockIndex: number;
  fragment?: ParagraphFragment;
  rowFragment?: RowFragment;
}

// Page distribution structure from document builder
interface PageDistribution {
  /** Sequential over every page; the React key. */
  pageNumber: number;
  /**
   * The number in "Page n of N". Report pages only -- an attached PDF's pages
   * carry no footer and are absent here (they are merged in after render, see
   * DocumentEditor.mergeAttachments), so it is the builder's count that is
   * printed, not react-pdf's.
   */
  reportPageNumber?: number;
  blockIndices: number[];
  items?: PageItem[];
  type: 'body' | 'appendix';
  appendixKind?: 'photo' | 'table' | 'plan' | 'attachment';
  appendixBlockId?: string;
  appendixPageIndex?: number;
  appendixTotalPages?: number;
  /** Which rows of a table appendix this page shows. */
  rowFragment?: RowFragment;
  /** Which photos of a photo appendix this page shows, and its layout. */
  photoFragment?: PhotoFragment;
}

interface DocumentPDFProps {
  document: Document;
  headerFields: DocumentHeaderFields;
  projectData: {
    job_number: number;
    description: string;
    street_address: string | null;
    city: string | null;
    state: string | null;
    postcode: string | null;
    country: string | null;
    care_of?: string | null;
    clients: {
      company_name: string;
      address_type: string | null;
      street_address: string | null;
      po_box: string | null;
      postal_address?: string | null;
      header_lines?: string | null;
      billing_email?: string | null;
      city: string | null;
      state: string | null;
      postcode: string | null;
    } | null;
    contacts: {
      first_name: string;
      last_name: string;
    } | null;
    // Named on a site instruction; ignored by the letter chrome.
    head_contractor?: { company_name: string } | null;
    stage?: {
      stage_id: string;
      stage_description: string;
      care_of?: string | null;
      head_contractor?: { company_name: string } | null;
      /** The stage's own primary contact; null inherits. Read via effectiveContact. */
      contacts?: DocumentContact | null;
    } | null;
  } | null;
  /**
   * How page 1 is drawn. Comes from the template; each variant has a matching
   * renderer in DocumentChrome so the editor and the PDF agree.
   */
  chrome?: DocumentChromeVariant;
  /**
   * Whether the Re: block carries the project address line. DocumentChrome is
   * given the same value; a line one surface draws and the other does not moves
   * the whole body 12.65pt in the export.
   */
  showSiteAddress?: boolean;
  /**
   * For a TEMPLATES_WITH_CERTIFICATE_FOOTER document, the `pageNumber` of the
   * page that carries the trustee line -- the last page drawn here. Chosen by
   * DocumentEditor, which puts it on the same page of its own, so the two can
   * never pick differently. Set means no page carries "Page X of Y".
   */
  certificateFooterPage?: number | null;
  signatoryName: string;
  // The signature to stamp into the signature block, once the document is
  // signed. imageUrl here is a base64 data URL (react-pdf cannot fetch).
  signatureStamp?: SignatureStamp | null;
  imageUrls?: Record<string, string>; // Map of storage paths to signed URLs
  pages?: PageDistribution[]; // Page distribution from document builder
  /** The N in "Page n of N": pages that carry a footer. Defaults to every page. */
  reportPageCount?: number;
  // Line layout of each paragraph as rendered by the builder (keyed by block id).
  // When present, lines are reproduced exactly instead of re-wrapped here.
  paragraphLayouts?: Record<string, ParagraphLayout>;
}

// =======================================
// RICH TEXT
// =======================================
//
// Paragraph content is the contenteditable's innerHTML. The selection toolbar
// applies bold/italic/underline/strikethrough/highlight/links via execCommand,
// so we parse those tags into styled runs instead of stripping them.

interface TextRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  sub?: boolean;
  backgroundColor?: string;
  href?: string;
}

const decodeEntities = (text: string): string =>
  text
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

// react-pdf's colour parser wants hex or names; execCommand writes rgb().
const normaliseColor = (value: string): string | undefined => {
  const trimmed = value.trim();
  const rgb = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const toHex = (n: string) => Number(n).toString(16).padStart(2, '0');
    return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`;
  }
  if (trimmed === 'transparent' || trimmed === '') return undefined;
  return trimmed;
};

export const parseRichText = (html: string): TextRun[] => {
  const runs: TextRun[] = [];
  const stack: Omit<TextRun, 'text'>[] = [{}];
  const tokenRe = /<\/?([a-zA-Z][a-zA-Z0-9]*)([^>]*)>|([^<]+)/g;
  let match: RegExpExecArray | null;

  const push = (text: string) => {
    if (!text) return;
    runs.push({ ...stack[stack.length - 1], text });
  };

  while ((match = tokenRe.exec(html)) !== null) {
    if (match[3] !== undefined) {
      push(decodeEntities(match[3]));
      continue;
    }
    const isClosing = match[0].startsWith('</');
    const tag = match[1].toLowerCase();
    const attrs = match[2] || '';

    if (tag === 'br') {
      push('\n');
      continue;
    }
    if (isClosing) {
      if (tag === 'div' || tag === 'p') {
        // Block-level breaks inside a paragraph become newlines
        if (runs.length > 0 && !runs[runs.length - 1].text.endsWith('\n')) push('\n');
      }
      if (stack.length > 1) stack.pop();
      continue;
    }

    const current = { ...stack[stack.length - 1] };
    switch (tag) {
      case 'b':
      case 'strong':
        current.bold = true;
        break;
      case 'i':
      case 'em':
        current.italic = true;
        break;
      case 'u':
        current.underline = true;
        break;
      case 's':
      case 'strike':
      case 'del':
        current.strike = true;
        break;
      case 'sub':
        current.sub = true;
        break;
      case 'a': {
        const href = attrs.match(/href\s*=\s*["']([^"']*)["']/i);
        if (href) current.href = decodeEntities(href[1]);
        break;
      }
      default: {
        const bg = attrs.match(/background-color\s*:\s*([^;"']+)/i);
        if (bg) current.backgroundColor = normaliseColor(bg[1]);
        break;
      }
    }
    stack.push(current);
  }

  // Trim a single trailing newline that a closing <div> may have added
  const last = runs[runs.length - 1];
  if (last && last.text === '\n') runs.pop();
  return runs;
};

/**
 * A subscript run: smaller, and dropped by react-pdf's own `'sub'` offset,
 * which is the depth the editor's CSS is set to (subscript.ts). It sets a size
 * and no lineHeight, so it inherits the line's absolute leading and the line
 * keeps its height -- as the editor's does.
 *
 * `basePt` is the size of the text the run sits in; react-pdf has no relative
 * font size, so every caller says what it is.
 */
const subscriptStyle = (basePt: number) => ({
  fontSize: basePt * SUBSCRIPT_SIZE_RATIO,
  verticalAlign: 'sub' as const,
});

/**
 * A plain-text field's value (`M_{d}` markup) as <Text> children: the string
 * itself when it holds no subscript, so every field without one renders
 * exactly as before.
 */
const withSubscripts = (value: string | null | undefined, basePt: number = FONT_SIZE_PT): React.ReactNode => {
  if (!value || !hasSubscripts(value)) return value;
  return parseSubscripts(value).map((segment, i) => (
    segment.sub
      ? <Text key={i} style={subscriptStyle(basePt)}>{segment.text}</Text>
      : <React.Fragment key={i}>{segment.text}</React.Fragment>
  ));
};

/**
 * A <Text> that never breaks a word where its styling changes.
 *
 * react-pdf splits a word into a separate piece at every change of run --
 * "V" and its subscript "u", or a bold "M" before a plain "d" -- and puts a
 * hyphenation point between the pieces. With hyphenation off (utils/pdfFonts)
 * that is the only kind of hyphenation point left, and taking it printed
 * "V-" at the end of one line and the subscript "u" at the start of the next.
 * Chrome never breaks a word there. A penalty of linebreak.infinity (10000)
 * makes textkit's line breaker skip those points, so a word breaks only at a
 * space, as in the editor. Every Text that can hold a subscript is one of
 * these. The prop is real but missing from react-pdf's types.
 */
type TextProps = React.ComponentProps<typeof Text>;
const KEEP_WORDS_WHOLE = { hyphenationPenalty: 10000 } as unknown as Partial<TextProps>;
const WordText: React.FC<TextProps> = (props) =>
  React.createElement(Text, { ...props, ...KEEP_WORDS_WHOLE } as TextProps);

const renderRuns = (runs: Array<TextRun | LayoutRun>, basePt: number = FONT_SIZE_PT): React.ReactNode =>
  runs.map((run, i) => {
    const decorations = [run.underline ? 'underline' : null, run.strike ? 'line-through' : null].filter(Boolean);
    const style = {
      fontWeight: run.bold ? ('bold' as const) : undefined,
      fontStyle: run.italic ? ('italic' as const) : undefined,
      textDecoration: decorations.length > 0 ? (decorations.join(' ') as 'underline' | 'line-through') : undefined,
      backgroundColor: run.backgroundColor,
      ...(run.sub ? subscriptStyle(basePt) : {}),
    };
    if (run.href) {
      return (
        <Link key={i} src={run.href} style={{ ...style, color: COLOR_PRIMARY, textDecoration: 'underline' }}>
          {run.text}
        </Link>
      );
    }
    return (
      <Text key={i} style={style}>
        {run.text}
      </Text>
    );
  });

// A browser-measured line. Justification is reproduced by giving every space
// glyph the extra advance Chrome used (react-pdf ignores wordSpacing, but its
// letterSpacing is applied to each glyph of a run, so a run holding only spaces
// carries the extra exactly).
const renderMeasuredLine = (line: { runs: LayoutRun[]; wordSpacingPt: number }, basePt: number = FONT_SIZE_PT): React.ReactNode => {
  if (line.wordSpacingPt <= 0) return renderRuns(line.runs, basePt);
  const pieces: Array<LayoutRun & { isSpace?: boolean }> = [];
  for (const run of line.runs) {
    for (const part of run.text.split(/( +)/)) {
      if (!part) continue;
      pieces.push({ ...run, text: part, isSpace: /^ +$/.test(part) });
    }
  }
  return pieces.map((piece, i) => {
    if (!piece.isSpace) return <React.Fragment key={i}>{renderRuns([piece], basePt)}</React.Fragment>;
    const style = {
      letterSpacing: line.wordSpacingPt,
      backgroundColor: piece.backgroundColor,
      textDecoration: piece.underline ? ('underline' as const) : undefined,
    };
    return (
      <Text key={i} style={style}>
        {piece.text}
      </Text>
    );
  });
};

// =======================================
// HELPER FUNCTIONS
// =======================================

// Get image width in points based on size setting (ImageBlock sizePercent)
const getImageWidthPt = (size?: string): number => {
  switch (size) {
    case 'small': return CONTENT_WIDTH_PT * 0.33;
    case 'medium': return CONTENT_WIDTH_PT * 0.5;
    case 'large': return CONTENT_WIDTH_PT * 0.75;
    case 'full':
    default: return CONTENT_WIDTH_PT;
  }
};

// Get image height based on aspect ratio (width is determined by layout)
const getImageHeightForWidth = (aspect?: string, width: number = CONTENT_WIDTH_PT): number => {
  switch (aspect) {
    case 'horizontal-4-3':
      return width * (3/4);
    case 'horizontal-16-9':
      return width * (9/16);
    case 'vertical-3-4':
      return width * (4/3);
    case 'vertical-9-16':
      return width * (16/9);
    case 'square':
      return width;
    default: // Fallback to 4:3
      return width * (3/4);
  }
};

/**
 * The frame every picture of an image block covers, in points: what the
 * renderer draws, and what the export resamples each picture to
 * (utils/images). One function, so the embedded pixels are sized for the
 * box they are drawn in. `imageCount` is how many pictures the block HOLDS,
 * fetched or not: a block of one is drawn single whatever its layout. The
 * size default is 'medium', the editor's (ImageBlock), not 'full'.
 */
export const imageBlockFramePt = (data: ImageBlockData, imageCount: number): { widthPt: number; heightPt: number } => {
  const aspect = data.aspect || 'horizontal-4-3';
  const layout = data.layout || 'single';
  if (layout === 'single' || imageCount === 1) {
    const widthPt = getImageWidthPt(data.size || 'medium');
    return { widthPt, heightPt: getImageHeightForWidth(aspect, widthPt) };
  }
  // Multi-image grids use the full content width with an 8px gap (image-block.scss)
  const columns = layout === 'two-column' ? 2 : 3;
  const widthPt = (CONTENT_WIDTH_PT - px(8) * (columns - 1)) / columns;
  return { widthPt, heightPt: getImageHeightForWidth(aspect, widthPt) };
};

// =======================================
// BLOCK RENDERERS
// =======================================

interface RenderContext {
  imageUrls?: Record<string, string>;
  signatoryName?: string;
  signatureStamp?: SignatureStamp | null;
  paragraphLayouts?: Record<string, ParagraphLayout>;
}

// Cut paragraph HTML at character offsets (needs a DOM; Node renders whole)
const sliceParagraphHtml = (html: string, start: number, end: number): string =>
  typeof document === 'undefined' ? html : sliceHtml(html, start, end);

const renderBlockContent = (
  block: Block,
  ctx: RenderContext,
  fragment?: ParagraphFragment,
  rowFragment?: RowFragment
): React.ReactNode => {
  const { imageUrls, signatoryName, signatureStamp, paragraphLayouts } = ctx;
  switch (block.type) {
    case 'blank_page':
      // A marker with no height on either surface; the page break it causes
      // is the builder's, in the page plan.
      return null;

    case 'heading': {
      const data = block.data as HeadingBlockData;
      const styleKey = `heading${data.level}` as 'heading1' | 'heading2' | 'heading3';
      return (
        <WordText style={styles[styleKey] || styles.heading2}>
          {withSubscripts(data.text, HEADING_SIZE_PT[data.level] ?? HEADING_SIZE_PT[2])}
        </WordText>
      );
    }

    case 'paragraph': {
      const data = block.data as ParagraphBlockData;

      // Preferred path: the builder measured this paragraph's lines, so render
      // them one by one exactly as Chrome laid them out.
      const layout = paragraphLayouts?.[block.id];
      if (layout) {
        // A fragment shows only its lines of the paragraph
        const lines = fragment ? layout.slice(fragment.lineStart, fragment.lineEnd) : layout;
        if (lines.length === 0 || lines.every(line => line.runs.length === 0)) {
          return <View style={styles.paragraph} />;
        }
        return (
          <View style={styles.paragraphMeasured}>
            {lines.map((line, i) => (
              // A blank line (two forced breaks) keeps its height
              line.runs.length === 0
                ? <View key={i} style={{ height: LINE_PT }} />
                : <WordText key={i}>{renderMeasuredLine(line)}</WordText>
            ))}
          </View>
        );
      }

      // Fallback (no browser measurement available): let react-pdf wrap the text
      const html = fragment
        ? sliceParagraphHtml(data.content || '', fragment.charStart, fragment.charEnd)
        : (data.content || '');
      const runs = parseRichText(html);
      const hasText = runs.some(r => r.text.trim().length > 0);
      // An empty paragraph is a blank line in the builder (min-height one line);
      // keep the same height here instead of dropping it.
      if (!hasText) {
        return <View style={styles.paragraph} />;
      }
      return (
        <View style={styles.paragraph}>
          <WordText style={styles.paragraphText}>{renderRuns(runs)}</WordText>
        </View>
      );
    }

    case 'scope_list': {
      const data = block.data as ScopeListBlockData;
      const showCheckboxes = data.showCheckboxes !== false;

      return (
        <View>
          {data.items.map((item) => {
            const indentStyle = item.indent === 1
              ? styles.scopeIndent1
              : item.indent === 2
                ? styles.scopeIndent2
                : item.indent === 3
                  ? styles.scopeIndent3
                  : {};

            if (showCheckboxes) {
              return (
                <View key={item.id} style={[styles.scopeItem, indentStyle]}>
                  <View style={[styles.checkbox, item.completed ? styles.checkboxChecked : {}]} />
                  <WordText style={[styles.scopeText, item.completed ? styles.completedText : {}]}>
                    {withSubscripts(item.text)}
                  </WordText>
                </View>
              );
            }

            const bullet = item.indent === 0 ? '•' : item.indent === 1 ? '◦' : item.indent === 2 ? '▪' : '▫';
            return (
              <View key={item.id} style={[styles.scopeItem, indentStyle]}>
                <Text style={styles.scopeBullet}>{bullet}</Text>
                <WordText style={styles.scopeText}>{withSubscripts(item.text)}</WordText>
              </View>
            );
          })}
        </View>
      );
    }

    case 'bullet_list': {
      const data = block.data as BulletListBlockData;
      return (
        <View>
          {data.items.map((item, i) => (
            <View key={i} style={styles.listItem}>
              <Text style={styles.listBullet}>{listMarker(listStyleOf(data), i)}</Text>
              <WordText style={styles.listContent}>{withSubscripts(item)}</WordText>
            </View>
          ))}
        </View>
      );
    }

    case 'table': {
      const data = block.data as TableBlockData;
      const colCount = data.rows[0]?.length || 1;
      const widths = data.columnWidths && data.columnWidths.length === colCount
        ? data.columnWidths
        : Array(colCount).fill(100 / colCount);
      return (
        <View style={styles.table}>
          {data.rows.map((row, rowIndex) => (
            <View key={rowIndex} style={styles.tableRow}>
              {row.map((cell, cellIndex) => {
                const isHeader = (rowIndex === 0 && data.hasHeaderRow) || cell.header;
                return (
                  <View
                    key={cellIndex}
                    style={[
                      styles.tableCell,
                      { width: `${widths[cellIndex]}%` },
                      isHeader ? styles.tableHeaderCell : {}
                    ]}
                  >
                    <WordText>{withSubscripts(cell.content)}</WordText>
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      );
    }

    case 'divider': {
      return <View style={styles.divider} />;
    }

    case 'spacer': {
      const data = block.data as SpacerBlockData;
      const spacerStyle = data.height === 'small'
        ? styles.spacerSmall
        : data.height === 'large'
          ? styles.spacerLarge
          : styles.spacerMedium;
      return <View style={spacerStyle} />;
    }

    case 'image': {
      const data = block.data as ImageBlockData & { images?: Array<{ id?: string; storagePath?: string }> };
      const alignment = data.alignment || 'center';
      const layout = data.layout || 'single';

      // Every picture the block holds, in order, with its data URL or null
      // when its download failed. A failed picture leaves its grid cell
      // empty rather than closing up the grid, as the photo appendix does:
      // the others keep the size the editor showed them at, which is the
      // size the export resampled them for.
      const slots: (string | null)[] = [data.storagePath, ...(data.images || []).map(img => img.storagePath)]
        .filter((path): path is string => Boolean(path))
        .map(path => imageUrls?.[path] ?? null);
      const fetched = slots.filter((url): url is string => url !== null);

      if (fetched.length === 0) {
        return null; // No images to render
      }

      const frame = imageBlockFramePt(data, slots.length);

      // Single image layout
      if (layout === 'single' || slots.length === 1) {
        const { widthPt: imgWidth, heightPt: imgHeight } = frame;

        const containerStyle = alignment === 'left'
          ? styles.imageContainerLeft
          : alignment === 'right'
            ? styles.imageContainerRight
            : styles.imageContainer;

        return (
          <View style={containerStyle}>
            <View style={{ width: imgWidth }}>
              <View style={[styles.imageWrapper, { width: imgWidth, height: imgHeight }]}>
                <Image
                  src={fetched[0]}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </View>
              {data.caption ? (
                <WordText style={styles.imageCaption}>{withSubscripts(data.caption, IMAGE_CAPTION_SIZE_PT)}</WordText>
              ) : null}
            </View>
          </View>
        );
      }

      const gap = px(8);
      const columns = layout === 'two-column' ? 2 : 3;
      const { widthPt: colWidth, heightPt: colHeight } = frame;

      const rows: (string | null)[][] = [];
      for (let i = 0; i < slots.length; i += columns) {
        rows.push(slots.slice(i, i + columns));
      }

      return (
        <View>
          {rows.map((row, rowIndex) => (
            <View key={rowIndex} style={{ flexDirection: 'row', marginBottom: rowIndex < rows.length - 1 ? gap : 0 }}>
              {row.map((url, colIndex) => (
                <View key={colIndex} style={{ width: colWidth, marginRight: colIndex < row.length - 1 ? gap : 0 }}>
                  <View style={[styles.imageWrapper, { width: colWidth, height: colHeight }]}>
                    {url && (
                      <Image
                        src={url}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </View>
                </View>
              ))}
            </View>
          ))}
        </View>
      );
    }

    case 'image_appendix': {
      // Rendered on dedicated appendix pages (renderAppendixPage); nothing inline
      return null;
    }

    case 'instruction_list': {
      const data = block.data as InstructionListBlockData;
      const allRows = data.rows ?? [];
      // Indices are against the whole list so an item keeps its REF number on
      // a continuation page; the header repeats with every slice.
      const firstIndex = rowFragment ? rowFragment.rowStart : 0;
      const rows = rowFragment
        ? allRows.slice(rowFragment.rowStart, rowFragment.rowEnd)
        : allRows;
      return (
        <View>
          <View style={styles.siTableHead}>
            <Text style={[styles.siTableHeadCell, styles.siTableHeadRef]}>REF</Text>
            <Text style={styles.siTableHeadCell}>DESCRIPTION</Text>
          </View>
          {rows.map((row, offset) => {
            const index = firstIndex + offset;
            return (
            <View key={row.id} style={styles.siRow}>
              <Text style={[styles.siRowRef, styles.bold]}>{index + 1}.0</Text>
              <View style={styles.siRowContent}>
                {/* The item's own blocks, through the same renderer the report
                    body uses -- so a bullet list or heading inside an
                    instruction draws exactly as it does anywhere else, and its
                    paragraphs replay the line breaks the builder measured. */}
                {renderBlocks(
                  instructionRowBlocks(row).map(b => ({ block: b })),
                  ctx
                )}
              </View>
            </View>
            );
          })}
        </View>
      );
    }

    case 'drawing_list': {
      const data = block.data as DrawingListBlockData;
      const allRows = data.rows ?? [];
      const rows = rowFragment
        ? allRows.slice(rowFragment.rowStart, rowFragment.rowEnd)
        : allRows;
      // The lead-in is said once, so a continuation page carries the drawings
      // alone -- and DocumentEditor measured it apart from the rows for this.
      const isContinuation = !!rowFragment && rowFragment.part > 0;
      // Older certificates own their lead-in; newer ones carry it as
      // paragraphs above the block. Same test as DrawingListBlock.
      const showsLeadIn = data.showsLeadIn !== false && !isContinuation;
      return (
        <View>
          {showsLeadIn && (
            <>
              {/* Composed, never typed -- the same helper the editor block
                  calls, so the two surfaces cannot word it differently. */}
              <Text style={styles.drawingCriteria}>{designCriteriaSentence(data)}</Text>
              <Text style={styles.drawingIntro}>{DRAWINGS_INTRO}</Text>
            </>
          )}
          {allRows.length === 0 ? (
            <Text style={styles.drawingEmpty}>No drawings listed.</Text>
          ) : (
            rows.map(row => (
              <View key={row.id} style={styles.drawingRow}>
                {/* The same marker and column the bullet list uses. */}
                <Text style={styles.drawingBullet}>•</Text>
                <WordText style={styles.drawingNumber}>{withSubscripts(row.drawing_number)}</WordText>
                <Text style={styles.drawingIssue}>{`Issue ${row.issue_number}`}</Text>
                <WordText style={styles.drawingTitle}>{withSubscripts(row.title)}</WordText>
              </View>
            ))
          )}
        </View>
      );
    }

    case 'qa_list': {
      const data = block.data as QaListBlockData;
      const allRows = data.rows ?? [];
      const rows = rowFragment
        ? allRows.slice(rowFragment.rowStart, rowFragment.rowEnd)
        : allRows;
      return (
        <View>
          {allRows.length === 0 ? (
            <Text style={styles.qaEmpty}>No QA items listed.</Text>
          ) : (
            rows.map(row => (
              <View key={row.id}>
                <View style={styles.qaLine}>
                  {/* The same marker and column the bullet list uses. */}
                  <Text style={styles.qaBullet}>•</Text>
                  <WordText style={styles.qaText}>{withSubscripts(row.text)}</WordText>
                </View>
                {/* Null when the author removed the line; QaListBlock drops
                    it on screen in the same case. */}
                {row.detail !== null && (
                  <View style={styles.qaLine}>
                    {/* One level in, then the dash in a column of its own -- the
                        same width, so the two markers stack. */}
                    <View style={styles.qaIndent} />
                    <Text style={styles.qaBullet}>-</Text>
                    <WordText style={styles.qaText}>{withSubscripts(row.detail)}</WordText>
                  </View>
                )}
              </View>
            ))
          )}
        </View>
      );
    }

    case 'signature': {
      const data = block.data as SignatureBlockData;
      // Same math as the editor block: height = two text lines x the signer's
      // scale; offsets in mm from the space's bottom-left. react-pdf needs an
      // explicit width for an absolutely positioned image, computed from the
      // aspect ratio measured at export time.
      const sigHeightPt = LINE_PT * 2 * (signatureStamp?.scale ?? 1);
      const sigAspect = signatureStamp?.aspectRatio ?? null;
      // Bottom of the signature space, measured from the section top: closing
      // line (when there is one) + company line + the two-line space itself.
      // A site instruction has no closing, and react-pdf would still draw a
      // full line for an empty <Text> where the browser collapses it to zero --
      // so the line is omitted on both surfaces together.
      const sigSpaceBottomPt = LINE_PT * signatureSpaceLines(data.closing);

      // The distribution list, when the template has one, sits to the RIGHT of
      // the signature on the same line. It is part of this block rather than one
      // of its own so the editor measures the line once, at the height of the
      // taller column.
      const distribution = data.distribution;

      // The stamp is positioned against THIS view, so the signature keeps its
      // own View whether or not there is a distribution column beside it.
      const signatureColumn = (
        <View style={distribution ? [styles.signatureSection, styles.signatureFooterColumn] : styles.signatureSection}>
          {data.closing ? <Text>{data.closing}</Text> : null}
          <Text>{data.company}</Text>
          <View style={styles.signatureSpace}>
            {signatureStamp?.imageUrl && !sigAspect ? (
              // Aspect ratio unmeasurable: pre-placement fallback, contained
              // inside the space.
              <Image style={styles.signatureImageFallback} src={signatureStamp.imageUrl} />
            ) : null}
          </View>
          <View style={styles.signatureLine} />
          <Text style={styles.signatoryName}>{signatureStamp?.name || signatoryName || ''}</Text>
          {signatureStamp?.title ? <Text style={styles.signatoryMeta}>{signatureStamp.title}</Text> : null}
          {signatureStamp?.registration ? <Text style={styles.signatoryMeta}>{signatureStamp.registration}</Text> : null}
          {signatureStamp?.imageUrl && sigAspect ? (
            <Image
              src={signatureStamp.imageUrl}
              style={{
                position: 'absolute',
                left: mmToPt(signatureStamp.offsetXMm ?? 0),
                top: sigSpaceBottomPt - sigHeightPt + mmToPt(signatureStamp.offsetYMm ?? 0),
                height: sigHeightPt,
                width: sigHeightPt * sigAspect,
              }}
            />
          ) : null}
        </View>
      );

      if (!distribution) return signatureColumn;

      return (
        <View style={styles.signatureFooterRow}>
          {signatureColumn}
          <View>
            <Text style={styles.siDistributionLabel}>DISTRIBUTION</Text>
            <View style={styles.siDistributionItems}>
              {distribution.map(item => (
                <View key={item.id} style={styles.siDistributionRow}>
                  <Text>{item.label}</Text>
                  <View style={styles.siDistributionBox}>
                    {/* Drawn, not typed: Tinos has no "✓", so a text tick
                        printed nothing. See SI_TOKENS.distributionTickPath. */}
                    {item.checked ? (
                      <Svg
                        width={distributionTickInnerPt}
                        height={distributionTickInnerPt}
                        viewBox={`0 0 ${distributionTickInnerPt} ${distributionTickInnerPt}`}
                      >
                        <Path
                          d={SI_TOKENS.distributionTickPath}
                          fill="none"
                          stroke={SI_TOKENS.ink}
                          strokeWidth={SI_TOKENS.distributionTickStrokePt}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </Svg>
                    ) : null}
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>
      );
    }

    default:
      return null;
  }
};

// Every block is wrapped exactly like .block-wrapper in the builder. The blank
// line between two blocks exists only when the block above has "space below"
// AND the block below has "space above"; the first block on a page has none
// (.block-wrapper:first-child).
const renderBlocks = (
  items: Array<{ block: Block; fragment?: ParagraphFragment; rowFragment?: RowFragment }>,
  ctx: RenderContext
): React.ReactNode => {
  let prevBelow = false;
  let isFirst = true;
  return items.map(({ block, fragment, rowFragment }) => {
    const content = renderBlockContent(block, ctx, fragment, rowFragment);
    if (content === null) return null;
    const spacing = getBlockSpacing(block);
    const marginTop = !isFirst && spacing.above && prevBelow ? LINE_PT : 0;
    prevBelow = spacing.below;
    isFirst = false;
    return (
      <View
        key={
          fragment ? `${block.id}#${fragment.part}`
            : rowFragment ? `${block.id}#i${rowFragment.part}`
            : block.id
        }
        style={[styles.blockWrapper, { marginTop }]}
      >
        {content}
      </View>
    );
  });
};

const PageFooter: React.FC<{ pageNumber: number; totalPages: number }> = ({ pageNumber, totalPages }) => (
  <View style={styles.pageFooter} fixed>
    <Text>Page {pageNumber} of {totalPages}</Text>
  </View>
);

// =======================================
// MAIN COMPONENT
// =======================================

const DocumentPDF: React.FC<DocumentPDFProps> = ({
  document,
  headerFields,
  projectData,
  chrome = 'letter',
  showSiteAddress = true,
  certificateFooterPage = null,
  signatoryName,
  signatureStamp,
  imageUrls = {},
  pages,
  reportPageCount,
  paragraphLayouts
}) => {
  const renderContext: RenderContext = { imageUrls, signatoryName, signatureStamp, paragraphLayouts };
  // A certificate's trustee line replaces the page numbers outright.
  const showPageNumbers = certificateFooterPage === null;
  const certificateFooter = (
    <Image src={`${ASSET_BASE}/${CERTIFICATE_FOOTER_SRC}`} style={styles.certificateFooter} />
  );
  // Display values that incorporate stage info when the document is for a stage
  const displayJobNumber = displayJobNumberOf(projectData);
  const displayProjectDescription = displayProjectDescriptionOf(projectData);

  // Client address block: exactly the invoice's rules (header lines, care-of
  // "c/-" from stage then project, street / postal / email formats, city line).
  const client = projectData?.clients ?? null;
  const clientAddress = client
    ? buildClientAddressLines(
        {
          ...client,
          postal_address: client.postal_address || (client.po_box ? `PO Box ${client.po_box}` : null),
          header_lines: client.header_lines ?? null,
          billing_email: client.billing_email ?? null,
        },
        resolveCareOf(null, projectData?.stage, projectData)
      )
    : null;

  // Site/project address: single line, street + city only.
  const displaySiteAddress = projectData
    ? [projectData.street_address, projectData.city].filter(Boolean).join(', ')
    : '';

  // Primary contact pulled live: the stage's own if it has one, else the
  // project's. Overrides win when set.
  const liveContact = effectiveContact(projectData);
  const projectContactFull = liveContact
    ? `${liveContact.first_name} ${liveContact.last_name}`.trim()
    : '';
  const projectContactFirst = liveContact?.first_name || '';
  const contactDisplay = headerFields.contact_name || projectContactFull;
  const dearDisplay = headerFields.dear_name || projectContactFirst;

  // Head contractor: the stage's, falling back to the project's -- the same
  // precedence resolveCareOf uses. Blank for Advisory and RWD, which have no
  // such field; the user types it in the Fields sidebar instead.
  const headContractorName =
    projectData?.stage?.head_contractor?.company_name ||
    projectData?.head_contractor?.company_name ||
    '';

  /**
   * A long date with its ordinal suffix superscripted: 5(th) May 2026.
   * Counterpart of LongDate in DocumentChrome.
   */
  const renderLongDate = (
    parts: LongDateParts,
    ordinalStyle: PdfStyle,
    textStyle?: PdfStyle,
  ) => (
    <View style={styles.longDate}>
      <Text style={textStyle}>{parts.day}</Text>
      <Text style={textStyle ? [textStyle, ordinalStyle] : ordinalStyle}>{parts.ordinal}</Text>
      <Text style={textStyle}>{parts.rest}</Text>
    </View>
  );

  const renderLetterHeader = () => (
    <View style={styles.headerSection}>
      <View style={styles.headerRow}>
        {renderLongDate(
          formatDateLongParts(headerFields.document_date || document.created_at),
          styles.dateOrdinal,
        )}
        <Text>Project No: {displayJobNumber}</Text>
      </View>

      <View style={styles.clientSection}>
        {clientAddress ? (
          <>
            {clientAddress.headerLines.map((line, i) => (
              <Text key={`hdr-${i}`}>{line}</Text>
            ))}
            <Text>{clientAddress.topName}</Text>
            {clientAddress.careOfClientName && (
              <Text>c/- {clientAddress.careOfClientName}</Text>
            )}
            {clientAddress.addressLines.map((line, i) => (
              <Text key={`addr-${i}`}>{line}</Text>
            ))}
            {clientAddress.cityLine && (
              <Text style={[styles.clientAddress, styles.bold]}>{clientAddress.cityLine}</Text>
            )}
          </>
        ) : (
          <Text>—</Text>
        )}
      </View>

      {/* "ATTENTION:" followed by three spaces, as typed in the letter */}
      <Text style={styles.attentionRow}>{'ATTENTION:   '}{contactDisplay}</Text>

      <View style={styles.dearRow}>
        <Text>Dear {dearDisplay},</Text>
      </View>

      <View style={styles.referenceRow}>
        <Text style={styles.referenceLabel}>Re:</Text>
        <View style={styles.referenceContent}>
          <Text style={styles.referenceLine}>{headerFields.report_title}</Text>
          <Text style={styles.referenceLine}>{displayProjectDescription}</Text>
          {showSiteAddress && (
            <Text style={styles.referenceLine}>{displaySiteAddress}</Text>
          )}
        </View>
      </View>
    </View>
  );

  // Site instruction chrome. Its counterpart in the editor is
  // DocumentChrome's SiteInstructionHeader; both take every size, colour and
  // gap from siChromeTokens so neither surface holds its own copy.
  //
  // Values fall back to the live project only while the document is a draft --
  // once issued, header_fields holds the snapshot and these overrides are set.
  const renderSiteInstructionHeader = () => {
    // `fill` only for the two cells that share the top-right pair. A flex child
    // in a column wrapper is sized from the wrapper rather than its own content
    // in react-pdf, which collapsed the rows to half their height.
    //
    // A node value is placed straight into the rule, NOT wrapped in the <Text>
    // a string gets. Inside a <Text>, react-pdf gives every descendant run the
    // parent's baseline instead of laying a <View> out as its own box -- which
    // is the box the date's superscript is raised from, so wrapping it printed
    // the suffix small but flat on the baseline, and only in the PDF.
    const cell = (label: string, value: React.ReactNode, placeholder: string, fill = false) => (
      <View key={label} style={fill ? styles.siFieldCellFill : undefined}>
        <Text style={styles.siFieldLabel}>{label}</Text>
        <View style={styles.siFieldRule}>
          {typeof value === 'string' || !value ? (
            <Text style={styles.siFieldValue}>{value || placeholder}</Text>
          ) : (
            value
          )}
        </View>
      </View>
    );

    // Three rows of two. The right column's top cell carries the two short
    // reference numbers side by side, which frees its lower cells for the date
    // and the method. Mirrored by SiteInstructionHeader in DocumentChrome.
    const leftColumn: [string, string, string][] = [
      ['PROJECT', headerFields.project || displayProjectDescription, '[Project]'],
      ['CLIENT / ARCHITECT', headerFields.client_architect || client?.company_name || '', '[Client]'],
      ['CONTRACTOR', headerFields.contractor || headContractorName, '[Contractor]'],
    ];
    const rightColumn: React.ReactNode[] = [
      (
        <View key="numbers" style={styles.siFieldPair}>
          {cell('PROJECT NO', headerFields.project_no || displayJobNumber, '[Project No]', true)}
          <View style={styles.siFieldPairGap} />
          {cell('SI NO', document.sequence_number != null ? String(document.sequence_number) : '', '[SI No]', true)}
        </View>
      ),
      cell(
        'DATE',
        renderLongDate(
          formatDateLongParts(headerFields.document_date || document.created_at),
          styles.siDateOrdinal,
          styles.siFieldValue,
        ),
        '[Date]',
      ),
      cell('METHOD', headerFields.method || '', '[Method]'),
    ];

    return (
      <View style={styles.siHeader}>
        <Text style={styles.siTitle}>SITE INSPECTION RECORD</Text>

        <View style={styles.siFields}>
          {[0, 1, 2].map(row => (
            // The editor's grid uses row-gap, which falls BETWEEN rows only.
            // Giving the last row a bottom margin here made the PDF's field
            // grid 16pt taller than the one the builder measured, so page 1
            // had 16pt less room than it believed.
            <View key={row} style={row === 2 ? styles.siFieldRowLast : styles.siFieldRow}>
              <View style={styles.siFieldCellWide}>
                {cell(...leftColumn[row])}
              </View>
              <View style={styles.siFieldCellNarrow}>
                {rightColumn[row]}
              </View>
            </View>
          ))}
        </View>

        <View style={styles.siNotice}>
          <Text style={styles.siNoticeText}>
            In accordance with the Conditions of the Contract between the Proprietor and the
            Contractor, the Site Inspection Record set out below is hereby issued to the Contractor.
          </Text>
          <Text style={[styles.siNoticeText, styles.siNoticeTextSecond]}>
            Should the Contractor consider that this instruction involves any cost adjustment or
            time extension, the contractor shall inform the Superintendent before complying with
            the instruction.
          </Text>
        </View>
      </View>
    );
  };

  const renderHeader = () =>
    chrome === 'site_instruction' ? renderSiteInstructionHeader() : renderLetterHeader();

  // If no page distribution provided, let react-pdf flow the content itself (legacy behaviour)
  if (!pages || pages.length === 0) {
    return (
      <PDFDocument>
        <Page size="A4" style={styles.page} wrap>
          <Image src={`${ASSET_BASE}/letterhead.png`} style={styles.letterhead} />
          {renderHeader()}
          <View>
            {renderBlocks(document.content.map(block => ({ block })), renderContext)}
          </View>
          {/* No certificate footer on this path: certificateFooterPage names a
              page of the page plan, and this path runs only without one. */}
          {showPageNumbers && (
            <View style={styles.pageFooter} fixed>
              <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </View>
          )}
        </Page>
      </PDFDocument>
    );
  }

  const totalPages = reportPageCount ?? pages.length;
  // Every photo's number, once for the whole export: the table and plan
  // pages both read it.
  const photoNumberMap = photoNumbers(document.content);

  /**
   * A table appendix page: the title above the first slice, the column header
   * above every slice, and the rows of this slice. Mirrors TableAppendixPage
   * to the token; cells replay the builder's measured lines (keyed by the
   * cell's layout key) so a narrow column wraps where Chrome wrapped it.
   */
  const renderTableAppendixPage = (page: PageDistribution) => {
    const block = document.content.find(b => b.id === page.appendixBlockId);
    if (!block || block.type !== 'table_appendix') return null;
    const data = block.data as TableAppendixBlockData;
    const allRows = data.rows || [];
    // A hidden Item or Photo column is off the page, as in the editor.
    const columns = visibleTableColumns(data.columns || []);
    const fragment = page.rowFragment;
    const isFirst = !fragment || fragment.part === 0;
    const firstIndex = fragment ? fragment.rowStart : 0;
    const rows = fragment ? allRows.slice(fragment.rowStart, fragment.rowEnd) : allRows;
    const numbers = photoNumberMap;
    const items = itemNumbers(allRows);

    const cellText = (key: string, text: string, style: PdfStyle | PdfStyle[]) => {
      const layout = paragraphLayouts?.[key];
      if (layout && layout.length > 0) {
        return (
          <View>
            {layout.map((line, i) => (
              line.runs.length === 0
                ? <View key={i} style={{ height: APPENDIX_TOKENS.cellSizePt * APPENDIX_TOKENS.lineHeight }} />
                : <WordText key={i} style={style}>{renderMeasuredLine(line, APPENDIX_TOKENS.cellSizePt)}</WordText>
            ))}
          </View>
        );
      }
      return <WordText style={style}>{withSubscripts(text, APPENDIX_TOKENS.cellSizePt)}</WordText>;
    };

    return (
      <View style={styles.appendixContent}>
        {isFirst && (
          <View style={{ alignSelf: 'flex-start' }}>
            <WordText style={styles.apxTitle}>{withSubscripts(data.title, APPENDIX_TOKENS.titleSizePt) || ' '}</WordText>
          </View>
        )}
        <View style={styles.apxTable}>
          <View style={styles.apxHead}>
            {columns.map(column => (
              <View key={column.id} style={[styles.apxCell, { width: `${column.widthPct}%` }]}>
                <WordText style={styles.apxHeadText}>{withSubscripts(column.label, APPENDIX_TOKENS.headSizePt)}</WordText>
              </View>
            ))}
          </View>
          {rows.map((row, offset) => {
            const index = firstIndex + offset;
            const item = items[index];
            if (row.kind === 'section') {
              return (
                <View key={row.id} style={styles.apxRow}>
                  <View style={[styles.apxCell, { width: '100%' }]}>
                    <WordText style={[styles.apxCellText, styles.apxBold]}>{withSubscripts(row.heading, APPENDIX_TOKENS.cellSizePt) || ' '}</WordText>
                  </View>
                </View>
              );
            }
            const zebra = item != null && item % 2 === 0;
            return (
              <View key={row.id} style={zebra ? [styles.apxRow, styles.apxRowZebra] : styles.apxRow}>
                {columns.map(column => {
                  const width = `${column.widthPct}%`;
                  if (column.kind === 'item') {
                    return (
                      <View key={column.id} style={[styles.apxCell, { width }]}>
                        <Text style={[styles.apxCellText, styles.apxBold]}>{item ?? ' '}</Text>
                      </View>
                    );
                  }
                  const text = column.kind === 'image'
                    ? tableImageCell(row, column, numbers)
                    : (row.cells[column.id] || '');
                  return (
                    <View key={column.id} style={[styles.apxCell, { width }]}>
                      {cellText(tableCellLayoutKey(block.id, row.id, column.id), text || ' ', styles.apxCellText)}
                    </View>
                  );
                })}
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  /**
   * The location plan page. The plan image is fitted into the same box the
   * editor fits it into (fitPlanBox, from the recorded pixel size), and the
   * markers are an Svg in POINTS over it, drawn from the same pre-rotated
   * geometry -- react-pdf ignores an SVG transform, so nothing here rotates.
   */
  const renderPlanAppendixPage = (page: PageDistribution) => {
    const block = document.content.find(b => b.id === page.appendixBlockId);
    if (!block || block.type !== 'plan_appendix') return null;
    const data = block.data as PlanAppendixBlockData;
    const pageIndex = page.appendixPageIndex ?? 0;
    const plan = planPages(data)[pageIndex];
    if (!plan) return null;
    const box = fitPlanBox(plan.naturalWidth, plan.naturalHeight, PLAN_BOX_WIDTH_MM, PLAN_BOX_HEIGHT_MM, plan.align, plan.valign);
    const boxWPt = mmToPt(box.widthMm);
    const boxHPt = mmToPt(box.heightMm);
    const imageUrl = plan.storagePath ? imageUrls[plan.storagePath] : null;
    const title = pageIndex === 0 ? (plan.title ?? data.title) : plan.title;
    const numbers = photoNumberMap;
    const strokePt = mmToPt(APPENDIX_TOKENS.markerStrokeMm);
    const labelSize = APPENDIX_TOKENS.markerLabelSizePt;

    return (
      <View style={styles.appendixContent}>
        {/* One line, as the editor's apx-title--single-line: the plan box is
            sized for exactly one title line. */}
        <View style={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
          <WordText style={[styles.apxTitle, { maxLines: 1, textOverflow: 'ellipsis' }]}>{withSubscripts(title, APPENDIX_TOKENS.titleSizePt) || ' '}</WordText>
        </View>
        {imageUrl && (
          // The whole plan box: the image sits inside it at its fitted
          // offsets, and the marker layer covers all of it, as the editor's
          // does -- a pin may sit beside a narrow plan.
          <View style={{ width: mmToPt(PLAN_BOX_WIDTH_MM), height: mmToPt(PLAN_BOX_HEIGHT_MM), position: 'relative' }}>
            <Image
              src={imageUrl}
              style={{ position: 'absolute', left: mmToPt(box.xMm), top: mmToPt(box.yMm), width: boxWPt, height: boxHPt }}
            />
            <Svg
              width={mmToPt(PLAN_BOX_WIDTH_MM)}
              height={mmToPt(PLAN_BOX_HEIGHT_MM)}
              viewBox={`0 0 ${mmToPt(PLAN_BOX_WIDTH_MM)} ${mmToPt(PLAN_BOX_HEIGHT_MM)}`}
              style={{ position: 'absolute', top: 0, left: 0 }}
            >
              {plan.markers.map(marker => {
                const g = markerGeometry(marker, box);
                const cx = mmToPt(g.cx);
                const cy = mmToPt(g.cy);
                const r = mmToPt(g.r);
                const points = g.arrowPoints
                  .split(' ')
                  .map(pair => pair.split(',').map(v => mmToPt(Number(v)).toFixed(3)).join(','))
                  .join(' ');
                return (
                  <React.Fragment key={marker.id}>
                    <Polygon
                      points={points}
                      fill={APPENDIX_TOKENS.markerStroke}
                      stroke={APPENDIX_TOKENS.markerStroke}
                      strokeWidth={strokePt}
                      strokeLinejoin="round"
                    />
                    <Circle
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill={APPENDIX_TOKENS.markerFill}
                      stroke={APPENDIX_TOKENS.markerStroke}
                      strokeWidth={strokePt}
                    />
                    <Text
                      x={cx}
                      y={cy + labelSize * 0.35}
                      style={{ fontSize: labelSize, fontFamily: DOCUMENT_FONT, fontWeight: 'bold' }}
                      textAnchor="middle"
                      fill={APPENDIX_TOKENS.markerInk}
                    >
                      {markerLabel(marker, data, document.content, numbers)}
                    </Text>
                  </React.Fragment>
                );
              })}
            </Svg>
          </View>
        )}
      </View>
    );
  };

  // Helper to render appendix page content
  // The photo appendix: the photos and layout the editor's page plan gave
  // this page (photoFragment), and every box from photoAppendixLayout -- the
  // same helpers that size the editor's page.
  const renderAppendixPage = (page: PageDistribution) => {
    if (!page.appendixBlockId) return null;

    const appendixBlock = document.content.find(b => b.id === page.appendixBlockId);
    if (!appendixBlock) return null;

    const appendixData = appendixBlock.data as ImageAppendixBlockData;
    const allImages = appendixData.images || [];
    const pageIdx = page.appendixPageIndex || 0;
    const fragment = page.photoFragment ?? photoAppendixPages(appendixData)[pageIdx];
    if (!fragment) return null;
    const grid = photoGrid(fragment.layout, pageIdx === 0);

    // Offset photo numbers if this appendix continues from prior ones
    const appendixStartNumber = getAppendixStartNumber(document.content, page.appendixBlockId);

    // A photo whose picture could not be fetched leaves its cell empty rather
    // than closing up the grid, so every other photo stays where the editor
    // showed it.
    const pageImages = allImages.slice(fragment.start, fragment.end).map((img, idx) => ({
      ...img,
      imageUrl: img.storagePath ? imageUrls[img.storagePath] : null,
      number: appendixStartNumber + fragment.start + idx,
    }));

    const rows: typeof pageImages[] = [];
    for (let i = 0; i < pageImages.length; i += grid.columns) {
      rows.push(pageImages.slice(i, i + grid.columns));
    }

    return (
      <View style={styles.appendixContent}>
        {/* Title only on the first page, one line like the editor's */}
        {pageIdx === 0 && (
          <WordText style={[styles.apxTitle, { maxLines: 1, textOverflow: 'ellipsis' }]}>{withSubscripts(appendixData.title, APPENDIX_TOKENS.titleSizePt) || ' '}</WordText>
        )}

        <View style={styles.appendixGrid}>
          {rows.map((row, rowIndex) => (
            <View key={rowIndex} style={{
              flexDirection: 'row',
              height: grid.cellHeightPt,
              marginBottom: rowIndex < rows.length - 1 ? grid.gapPt : 0,
            }}>
              {row.map((img, colIndex) => {
                const frame = photoFrame(grid, img.aspect);
                return (
                  <View key={img.id || colIndex} style={{
                    width: grid.cellWidthPt,
                    height: grid.cellHeightPt,
                    marginRight: colIndex < grid.columns - 1 ? grid.gapPt : 0,
                  }}>
                    <View style={{ width: grid.slotWidthPt, height: grid.slotHeightPt, position: 'relative' }}>
                      {img.imageUrl && (
                        <View style={[styles.appendixImageWrapper, {
                          left: frame.leftPt,
                          top: frame.topPt,
                          width: frame.widthPt,
                          height: frame.heightPt,
                        }]}>
                          <Image
                            src={img.imageUrl}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </View>
                      )}
                    </View>
                    <View style={[styles.imageLabelRow, { width: photoLabelWidth(grid, frame) }]}>
                      <WordText style={[styles.imageLabelLine, { maxLines: APPENDIX_TOKENS.photoLabelLines, textOverflow: 'ellipsis' }]}>
                        <Text style={styles.photoLabel}>Photo {img.number}</Text>
                        {img.caption ? <>{': '}{withSubscripts(img.caption, APPENDIX_TOKENS.photoLabelSizePt)}</> : ''}
                      </WordText>
                    </View>
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </View>
    );
  };

  // Render pages based on distribution from document builder
  return (
    <PDFDocument>
      {pages.map((page, pageIndex) => {
        // An attached PDF's pages are not rendered here at all: react-pdf
        // cannot embed a PDF, so DocumentEditor.mergeAttachments copies the
        // real pages into this document afterwards, at the same positions.
        if (page.appendixKind === 'attachment') return null;

        // Handle appendix pages
        if (page.type === 'appendix') {
          return (
            <Page key={page.pageNumber} size="A4" style={styles.appendixPage} wrap={false}>
              {page.appendixKind === 'table'
                ? renderTableAppendixPage(page)
                : page.appendixKind === 'plan'
                  ? renderPlanAppendixPage(page)
                  : renderAppendixPage(page)}
              {showPageNumbers && (
                <PageFooter pageNumber={page.reportPageNumber ?? page.pageNumber} totalPages={totalPages} />
              )}
              {page.pageNumber === certificateFooterPage && certificateFooter}
            </Page>
          );
        }

        const isFirstBodyPage = pageIndex === 0 && page.type === 'body';

        // Handle body pages
        return (
          <Page key={page.pageNumber} size="A4" style={styles.page} wrap={false}>
            <View style={styles.pageBox}>
              {/* Letterhead on first page only */}
              {isFirstBodyPage && (
                <Image src={`${ASSET_BASE}/letterhead.png`} style={styles.letterhead} />
              )}

              <View style={styles.pageContent}>
                {/* Header only on first body page (page 1) */}
                {isFirstBodyPage && renderHeader()}

                {/* Body blocks for this page */}
                <View>
                  {renderBlocks(
                    (page.items ?? page.blockIndices.map((blockIndex): PageItem => ({ blockIndex })))
                      .map(item => ({
                        block: document.content[item.blockIndex],
                        fragment: item.fragment,
                        rowFragment: item.rowFragment,
                      }))
                      .filter(item => !!item.block),
                    renderContext
                  )}
                </View>
              </View>
            </View>

            {showPageNumbers && (
              <PageFooter pageNumber={page.reportPageNumber ?? page.pageNumber} totalPages={totalPages} />
            )}
            {page.pageNumber === certificateFooterPage && certificateFooter}
          </Page>
        );
      })}
    </PDFDocument>
  );
};

export default DocumentPDF;
